"use client"

import { autoUpdate, flip, offset, shift, useFloating } from "@floating-ui/react-dom"
import clsx from "clsx"
import { ReactNode, useId, useRef, useState } from "react"
import { useEventCallback } from "src/hooks/useEventCallback"

type TooltipContentProps = {
  anchor: HTMLDivElement | null
  id: string
  variant?: "light" | "dark"
  children: ReactNode
}

const TooltipContent = ({ anchor, id, variant, children }: TooltipContentProps) => {
  const { refs, floatingStyles } = useFloating({
    elements: { reference: anchor },
    middleware: [offset(8), flip(), shift()],
    whileElementsMounted: autoUpdate,
  })

  return (
    <span
      ref={refs.setFloating}
      style={floatingStyles}
      id={id}
      role="tooltip"
      className={clsx("z-tooltip w-max rounded px-2 py-1 text-center text-sm font-normal shadow", {
        "bg-default text-white": variant === "dark",
        "text-default bg-white": variant === "light",
      })}
    >
      {children}
    </span>
  )
}

export type TooltipProps = {
  text: string
  variant?: "light" | "dark"
  children: ReactNode
}

export const Tooltip = ({ text, variant = "dark", children }: TooltipProps) => {
  const [visible, setVisible] = useState(false)
  const id = useId()
  const ref = useRef<HTMLDivElement>(null)
  const show = useEventCallback(() => text && setVisible(true))
  const hide = useEventCallback(() => text && setVisible(false))

  return (
    <div
      // Desktop events are enough because we don't show tooltips on mobile.
      onKeyDown={(event) => (event.key === "Tab" ? hide() : show())}
      onMouseEnter={show}
      onMouseLeave={hide}
      onClick={hide}
      className="group relative inline-flex"
    >
      <div ref={ref} aria-describedby={id} className="flex">
        {children}
      </div>

      {text && visible && (
        <TooltipContent anchor={ref.current} id={id} variant={variant}>
          {text}
        </TooltipContent>
      )}
    </div>
  )
}
